/* istanbul ignore file */

import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useVisibilityObserver } from '@noths/polaris-client-react-hooks';
import { grid, maxWidthContainer } from '@noths/polaris-client-ribbons-base';
import { OutlineButtonLink, SkeletonCard } from '@noths/polaris-client-ribbons-design-system';
import { CurrencyCode, SimilarProduct } from '@noths/polaris-dev-ts-types';
import dynamic from 'next/dynamic';
import * as R from 'ramda';

import { ModalLoader } from 'src/components/molecules/ModalLoader';
import { sectionVisible } from 'src/redux/visibilityTracking';
import { Partner, Product } from 'src/types';
import * as copy from '../constants';
import { RelatedProductClickPayload } from '../modules';
import { ContactPartnerSubmitPayload } from '../modules/thunks';
import {
  FieldValidationErrorPayload,
  PARTNER_STOREFRONT_VISIBILITY_NAME,
  PartnerProductCardsVisiblePayload,
  PartnerStorefrontLinkClickPayload,
} from '../tracking/events';
import { ContactFormServerErrors } from '../types';
import { PartnerCard } from './PartnerCard';
import { PartnerProductCard } from './PartnerProductCard';
import * as styles from '../styles/PartnerStorefront.styles';

export const RELATED_PRICE = 'recommended-price';
export const RELATED_PREVIOUS_PRICE = 'recommended-previous-price';

const NUMBER_OF_PRODUCTS_TO_DISPLAY = 4;
const NUMBER_OF_PRODUCTS_PER_ROW = 2;

const ContactPartnerModal = dynamic(
  async () =>
    (
      await import(
        /* webpackChunkName: "ContactPartnerModal" */ '../components/ContactPartnerModal'
      )
    ).ContactPartnerModal,
  { loading: () => <ModalLoader /> },
);

export interface PartnerStorefrontProps {
  contactFormServerErrors: ContactFormServerErrors;
  isContactPartnerModalOpen: boolean;
  messageSentSuccessfully: boolean;
  partner: Partner;
  product: Product;
  currency: CurrencyCode;
  similarProducts: SimilarProduct[] | null;
  closeContactPartnerModal: VoidFunction;
  onContactPartnerSubmit: (payload: ContactPartnerSubmitPayload) => void;
  onContactPartnerSubmitError: (payload: FieldValidationErrorPayload) => void;
  onPartnerStorefrontLinkClick: (payload: PartnerStorefrontLinkClickPayload) => void;
  onPartnerProductCardVisible: (payload: PartnerProductCardsVisiblePayload) => void;
  onRelatedProductClick: (payload: RelatedProductClickPayload) => void;
  openContactPartnerModal: VoidFunction;
}

export const PartnerStorefront = ({
  closeContactPartnerModal,
  contactFormServerErrors,
  isContactPartnerModalOpen,
  messageSentSuccessfully,
  product,
  similarProducts,
  currency,
  onContactPartnerSubmit,
  onContactPartnerSubmitError,
  onPartnerStorefrontLinkClick,
  onPartnerProductCardVisible,
  openContactPartnerModal,
  onRelatedProductClick,
}: PartnerStorefrontProps) => {
  const dispatch = useDispatch();
  const { observedElementRef: partnerStorefrontRef } = useVisibilityObserver(() =>
    dispatch(sectionVisible({ sectionName: PARTNER_STOREFRONT_VISIBILITY_NAME })),
  );
  const { observedElementRef: partnerProductCard } = useVisibilityObserver(() =>
    setProductCardsViewed(true),
  );
  const products = similarProducts?.slice(0, NUMBER_OF_PRODUCTS_TO_DISPLAY) ?? [];
  const isLoading = products !== null && products.length === 0;
  const [productCardsViewed, setProductCardsViewed] = useState(false);

  useEffect(() => {
    if (productCardsViewed && !isLoading) {
      onPartnerProductCardVisible({ products });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, productCardsViewed]);

  return (
    <>
      <div ref={partnerStorefrontRef} css={[grid, maxWidthContainer, styles.storefrontGrid]}>
        <div css={styles.partner} data-is-multi-row={products.length > NUMBER_OF_PRODUCTS_PER_ROW}>
          <PartnerCard
            partner={product.partner}
            onContactSellerClick={openContactPartnerModal}
            onPartnerStorefrontLinkClick={onPartnerStorefrontLinkClick}
          />
        </div>

        <div
          ref={partnerProductCard}
          css={styles.products}
          data-is-multi-row={isLoading || products.length > NUMBER_OF_PRODUCTS_PER_ROW}
          data-is-single-product={!isLoading && products.length === 1}
        >
          {isLoading
            ? R.range(0, NUMBER_OF_PRODUCTS_TO_DISPLAY).map((i) => (
                // eslint-disable-next-line react/no-array-index-key
                <SkeletonCard
                  key={`skeleton-${i}`}
                  css={styles.skeletonLoaderWrapper}
                  imageStyle={styles.imageWrapper}
                />
              ))
            : products.map((relatedProduct) => (
                <PartnerProductCard
                  product={relatedProduct}
                  currency={currency}
                  onRelatedProductClick={onRelatedProductClick}
                  key={product.code}
                />
              ))}
        </div>
        <div css={styles.visitShop} data-testid={`${copy.VISIT_SHOP_CLASSNAME}-sm`}>
          <OutlineButtonLink
            href={product.partner.links?.[1]?.rel || ''}
            className={copy.VISIT_SHOP_CLASSNAME}
          >
            {copy.VISIT_SHOP}
          </OutlineButtonLink>
        </div>
      </div>
      {isContactPartnerModalOpen && (
        <ContactPartnerModal
          isModalOpen={isContactPartnerModalOpen}
          onModalClose={closeContactPartnerModal}
          product={product}
          onSubmit={onContactPartnerSubmit}
          onSubmitError={onContactPartnerSubmitError}
          messageSentSuccessfully={messageSentSuccessfully}
          serverErrors={contactFormServerErrors}
        />
      )}
    </>
  );
};
